/**
 * Campos da janela de execucao da GMUD.
 *
 * A janela pertence ao fuso do TENANT — e o ambiente dele que sofre a parada.
 * "02:00 as 04:00" e madrugada para quem sofre a mudanca, e quem define o que
 * e madrugada ali e o tenant, nao o operador. O fuso de quem digita serve so
 * para traduzir na tela.
 *
 * Quando o tenant nao tem fuso cadastrado, perguntamos em vez de adivinhar:
 * um default silencioso errado agenda a parada na hora errada e ninguem
 * percebe ate acontecer (spec 2026-08-06-janela-gmud-fuso-do-tenant, §2.2).
 */
import { Alert, Stack, Text } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { IconAlertTriangle } from '@tabler/icons-react'
import { TimezoneSelect } from '@/components/ui/TimezoneSelect'
import { formatInZone, wallClockToInstant, zoneLabel } from './tzWindow'

export interface JanelaValor {
  start: Date | null
  end: Date | null
}

interface Props {
  /** Fuso do tenant vindo do detalhe da GMUD. null = nao cadastrado. */
  tenantTimezone: string | null
  /** Fuso do usuario autenticado. null = nao definido. */
  userTimezone: string | null
  value: JanelaValor
  onChange: (v: JanelaValor) => void
  /** Chamado quando o tenant nao tem fuso e o usuario escolhe um. */
  onTimezoneChange: (tz: string | null) => void
}

/** Janela e valida quando ambos existem e o fim vem depois do inicio. */
export function janelaValida(v: JanelaValor): boolean {
  return Boolean(v.start && v.end && v.end.getTime() > v.start.getTime())
}

export function JanelaExecucaoFields({
  tenantTimezone,
  userTimezone,
  value,
  onChange,
  onTimezoneChange,
}: Props) {
  const semFuso = !tenantTimezone
  const tz = tenantTimezone ?? 'UTC'
  const mostraTraducao = Boolean(userTimezone) && userTimezone !== tenantTimezone

  function traducao(wall: Date | null): string | undefined {
    if (!wall || !mostraTraducao || semFuso || !userTimezone) return undefined
    const instante = wallClockToInstant(wall, tz)
    return `= ${formatInZone(instante, userTimezone)} no seu horário (${userTimezone})`
  }

  const fimInvalido = Boolean(
    value.start && value.end && value.end.getTime() <= value.start.getTime(),
  )

  return (
    <Stack gap="sm">
      {semFuso ? (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Stack gap="xs">
            <Text size="sm">
              Este tenant não tem fuso horário cadastrado. Escolha em qual fuso a
              janela deve ser interpretada — não vamos adivinhar, porque errar aqui
              agenda a parada na hora errada.
            </Text>
            <TimezoneSelect
              value={null}
              onChange={onTimezoneChange}
              label="Fuso da janela"
            />
          </Stack>
        </Alert>
      ) : (
        <Text size="sm" c="dimmed">
          Horários no fuso do tenant: <strong>{zoneLabel(tz)}</strong>
        </Text>
      )}

      <DateTimePicker
        label="Início"
        required
        disabled={semFuso}
        value={value.start}
        onChange={(d) => onChange({ ...value, start: d })}
        description={traducao(value.start)}
        minDate={new Date()}
        valueFormat="DD/MM/YYYY HH:mm"
      />

      <DateTimePicker
        label="Fim"
        required
        disabled={semFuso}
        value={value.end}
        onChange={(d) => onChange({ ...value, end: d })}
        description={traducao(value.end)}
        error={fimInvalido ? 'O fim precisa ser depois do início.' : undefined}
        minDate={value.start ?? new Date()}
        valueFormat="DD/MM/YYYY HH:mm"
      />
    </Stack>
  )
}
